/**
 * Budget API Service
 * Handles budget estimation and cost information endpoints
 */

export class BudgetService {
  constructor(apiClient, errorHandler) {
    this.apiClient = apiClient;
    this.handleApiError = errorHandler;
  }

  /**
   * Get budget estimation for a trip
   * @param {Object} params - Budget estimation parameters
   * @param {string} params.destination - Destination city or country
   * @param {string} params.origin - Origin city
   * @param {string} params.start_date - Trip start date (YYYY-MM-DD)
   * @param {string} params.end_date - Trip end date (YYYY-MM-DD)
   * @param {number} params.adults - Number of adults (default: 1)
   * @param {number} params.children - Number of children (default: 0)
   * @param {number} params.pets - Number of pets (default: 0)
   * @param {Array<string>} params.holiday_types - Holiday types (e.g., ['Beach', 'Family'])
   * @param {string} params.accommodation_preference - budget, mid-range, luxury
   * @param {string} params.activity_level - low, moderate, high
   * @returns {Promise<Object>} Budget estimation response
   */
  async getBudgetEstimation(params) {
    try {
      const response = await this.apiClient.post('/budget/estimate', params);
      return response.data;
    } catch (error) {
      console.error('Error getting budget estimation:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Save a budget plan as a saved trip
   * @param {Object} budgetPlan - Budget plan data
   * @param {string} budgetPlan.name - Name for the saved budget plan
   * @param {Object} budgetPlan.searchParams - Original search parameters
   * @param {Object} budgetPlan.budgetResult - Budget estimation result
   * @param {string} budgetPlan.notes - Optional notes
   * @returns {Promise<Object>} Saved trip response
   */
  async saveBudgetPlan(budgetPlan) {
    try {
      const response = await this.apiClient.post('/trips/', {
        name: budgetPlan.name,
        search_params: budgetPlan.searchParams,
        results: {
          type: 'budget_plan',
          ...budgetPlan.budgetResult
        },
        enriched_results: null,
        notes: budgetPlan.notes || '',
        is_public: false
      });
      return response.data;
    } catch (error) {
      console.error('Error saving budget plan:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Get general cost information for a destination
   * @param {string} destination - Destination name
   * @returns {Promise<Object>} Destination cost information
   */
  async getDestinationCostInfo(destination) {
    try {
      const response = await this.apiClient.get(`/budget/destinations/${encodeURIComponent(destination)}/cost-info`);
      return response.data;
    } catch (error) {
      console.error('Error getting destination cost info:', error);
      throw this.handleApiError(error);
    }
  }
}

export default BudgetService;